<template>
  <div class="platform-page">
    <HeadNav selectedLanguage="en" page="platform" />

    <main>
      <section class="hero">
        <div class="container hero-grid">
          <div class="hero-copy">
            <p v-if="page.hero.eyebrow" class="eyebrow">{{ page.hero.eyebrow }}</p>
            <h1>{{ page.hero.title }}</h1>
            <p v-if="page.hero.subtitle" class="subtitle">{{ page.hero.subtitle }}</p>
            <p
              v-if="page.hero.description"
              class="lead"
              v-html="page.hero.descriptionHtml || page.hero.description">
            </p>
            <ul v-if="page.hero.points && page.hero.points.length" class="hero-points">
              <li v-for="point in page.hero.points" :key="point">{{ point }}</li>
            </ul>
            <div class="hero-actions">
              <router-link class="btn btn-primary" to="/download/">
                {{ page.hero.ctaLabel }}
              </router-link>
              <a
                v-if="page.hero.secondaryCta"
                class="btn btn-secondary"
                :href="page.hero.secondaryCta.href">
                {{ page.hero.secondaryCta.label }}
              </a>
            </div>
          </div>
          <div v-if="page.hero.image" class="hero-visual">
            <img
              class="hero-platform-image"
              :src="page.hero.image"
              :alt="page.hero.imageAlt || page.hero.title"
              loading="eager"
              width="520"
              height="430">
          </div>
        </div>
      </section>

      <section
        v-for="(section, sectionIndex) in page.sections"
        :id="section.id"
        :key="section.id"
        class="section"
        :class="{ 'section-tint': sectionIndex % 2 === 1 }">
        <div class="container section-inner">
          <div v-if="section.title || section.intro" class="section-head">
            <h2 v-if="section.title">{{ section.title }}</h2>
            <p v-if="section.intro">{{ section.intro }}</p>
          </div>

          <template v-for="(block, blockIndex) in section.blocks">
            <h3
              v-if="block.type === 'heading'"
              :key="`heading-${blockIndex}`"
              class="block-heading">
              {{ block.text }}
            </h3>

            <p
              v-else-if="block.type === 'paragraph'"
              :key="`paragraph-${blockIndex}`"
              class="paragraph"
              v-html="block.html || block.text">
            </p>

            <ol
              v-else-if="block.type === 'ordered-list'"
              :key="`ordered-list-${blockIndex}`"
              class="content-list ordered-list">
              <li v-for="item in block.items" :key="item">{{ item }}</li>
            </ol>

            <ul
              v-else-if="block.type === 'unordered-list'"
              :key="`unordered-list-${blockIndex}`"
              class="content-list">
              <li v-for="item in block.items" :key="item">{{ item }}</li>
            </ul>

            <div
              v-else-if="block.type === 'cards'"
              :key="`cards-${blockIndex}`"
              class="card-grid">
              <article v-for="item in block.items" :key="item.title" class="info-card">
                <h3>{{ item.title }}</h3>
                <p>{{ item.text }}</p>
              </article>
            </div>

            <div
              v-else-if="block.type === 'steps'"
              :key="`steps-${blockIndex}`"
              class="steps">
              <article v-for="(item, itemIndex) in block.items" :key="item.title" class="step-card">
                <span>{{ itemIndex + 1 }}</span>
                <div>
                  <h3>{{ item.title }}</h3>
                  <p v-html="item.html || item.text"></p>
                </div>
              </article>
            </div>

            <div
              v-else-if="block.type === 'table'"
              :key="`table-${blockIndex}`"
              class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th v-for="header in block.headers" :key="header">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <router-link
            v-if="section.cta"
            class="btn btn-primary section-cta"
            to="/download/">
            {{ section.cta }}
          </router-link>
        </div>
      </section>

      <section class="section">
        <div class="container section-inner">
          <div class="section-head">
            <h2>{{ platformsTitle }}</h2>
            <p>{{ platformsLead }}</p>
          </div>
          <div class="platform-grid">
            <component
              :is="platform.to ? 'router-link' : 'div'"
              v-for="platform in platforms"
              :key="platform.name"
              class="platform-card"
              v-bind="platform.to ? { to: platform.to } : {}">
              <div class="platform-icon" v-html="platform.icon"></div>
              <span>{{ platform.name }}</span>
            </component>
          </div>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container section-inner">
          <div class="section-head">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div class="faq-list">
            <details v-for="item in page.faqs" :key="item.question" class="faq-item">
              <summary>{{ item.question }}</summary>
              <p>{{ item.answer }}</p>
            </details>
          </div>
        </div>
      </section>

      <section v-if="page.closing" class="closing">
        <div class="container closing-inner">
          <h2>{{ page.closing.title }}</h2>
          <p>{{ page.closing.text }}</p>
          <router-link class="btn btn-primary" to="/download/">
            {{ page.closing.cta }}
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';
import { secureVpnPlatformPages } from '@/data/secureVpnPlatformPages';

const platformIcon = {
  windows: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 5.5l8-1.2v7.2H3V5.5zm9-.9l9-1.3v9.4h-9V4.6zM3 13.2h8v7.3l-8-1.2v-6.1zm9 .2h9v9.3l-9-1.3V13.4z"/></svg>`,
  apple: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.7 12.6c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2-.1 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.9-2.2.9-1.3 1.3-2.5 1.3-2.6-.03-.01-2.5-1-2.5-3.6zM14.8 5.8c.6-.8 1.1-1.9.9-3-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.8-.9 2.9 1 .1 2-.5 2.6-1.3z"/></svg>`,
  linux: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.1 2.2c-.9 0-1.8.7-2.2 1.8-.3.8-.2 1.7.1 2.8l.2.7c-.8.8-1.4 1.9-1.6 3.1-.4 2 .2 3.8 1.5 5l-.4 1.5c-.4 1.4-.2 2.6.6 3.3.5.5 1.2.7 2 .7.7 0 1.4-.3 2-.8.6.5 1.3.8 2 .8.8 0 1.5-.2 2-.7.8-.7 1-1.9.6-3.3l-.4-1.5c1.3-1.2 1.9-3 1.5-5-.2-1.2-.8-2.3-1.6-3.1l.2-.7c.3-1.1.4-2 .1-2.8-.4-1.1-1.3-1.8-2.2-1.8-.6 0-1.2.3-1.7.8-.5-.5-1.1-.8-1.7-.8zm-1.7 3.1c.2 0 .5.1.8.4l.8.8.8-.8c.3-.3.6-.4.8-.4.2 0 .4.1.5.4.1.3.1.7-.1 1.3l-.2.7h-3.6l-.2-.7c-.2-.6-.2-1 0-1.3.1-.3.3-.4.4-.4zm.2 3.2h3l.1.4c.3 1.1.1 2.2-.6 3-.4.4-.9.7-1.5.8h-.1c-.6-.1-1.1-.4-1.5-.8-.7-.8-.9-1.9-.6-3l.2-.4zm-1.5 5.1c.4.3.9.5 1.4.6v1.8c0 .5-.1.9-.3 1.1-.1.1-.2.2-.4.2-.2 0-.3-.1-.4-.2-.3-.3-.4-.9-.2-1.6l.1-.4-.2-1.5zm5.4 0l-.2 1.5.1.4c.2.7.1 1.3-.2 1.6-.1.1-.2.2-.4.2-.2 0-.3-.1-.4-.2-.2-.2-.3-.6-.3-1.1v-1.8c.5-.1 1-.3 1.4-.6z"/></svg>`,
  android: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7.2 8.8l-1.4-2.4.9-.5 1.4 2.5c.9-.4 1.9-.6 2.9-.6s2 .2 2.9.6l1.4-2.5.9.5-1.4 2.4C17.1 9.8 18 11.3 18 13v5.5c0 .8-.7 1.5-1.5 1.5H16v2c0 .6-.4 1-1 1s-1-.4-1-1v-2h-4v2c0 .6-.4 1-1 1s-1-.4-1-1v-2h-.5c-.8 0-1.5-.7-1.5-1.5V13c0-1.7.9-3.2 2.2-4.2zM9.5 12.2a1 1 0 100 2 1 1 0 000-2zm5 0a1 1 0 100 2 1 1 0 000-2z"/></svg>`,
  ios: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.7 12.6c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2-.1 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.9-2.2.9-1.3 1.3-2.5 1.3-2.6-.03-.01-2.5-1-2.5-3.6z"/></svg>`,
  chrome: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 00-8.7 5h7.2A3.5 3.5 0 0115 9.3L18.7 3A10 10 0 0012 2zM3.1 8.5A10 10 0 0012 22a10 10 0 008.6-5H12a3.5 3.5 0 01-3-1.7L3.1 8.5zM12 15.5A3.5 3.5 0 1112 8.5a3.5 3.5 0 010 7zM13.8 12l4.7 8.1A10 10 0 0022 12c0-.7-.1-1.4-.2-2h-7.3c.2.6.3 1.3.3 2z"/></svg>`,
  firefox: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c1.4 0 3.3.7 4.5 1.6-.8.2-1.5.5-2.1.9 1.7.1 3.2 1 4.1 2.4-.6-.1-1.2-.1-1.8 0 1.4.8 2.4 2.2 2.7 3.8-1.1-1.5-2.8-2.4-4.7-2.4-2.7 0-4.1 1.7-4.5 2.4-.2-.8-.1-1.6.2-2.4C8.6 7 7.4 8.4 7 10c-.9-1.5-.8-3.5.2-5.2C5 6.4 3.8 9 3.8 11.8 3.8 17 8 21.2 13.2 21.2c4.4 0 8.1-3 9.1-7.1C23.5 8.7 19.4 2 12 2z"/></svg>`,
  edge: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C7.4 2 3.7 5.2 2.6 9.4c1.4-1.9 3.7-3.2 6.4-3.2 1.2 0 2.2.2 3.1.6-2.7.8-4.7 3.3-4.7 6.3 0 .5 0 1 .1 1.4A8.1 8.1 0 003.5 12C2.5 14.8 3.7 18.5 8 20.4c.8.4 1.7.6 2.6.7-.7-.5-1.2-1.3-1.2-2.2 0-1.5 1.3-2.5 3.4-2.5h7.6c.4-1 .6-2 .6-3.1C20.9 6.5 16.9 2 12 2zm1.4 17.8c-1.7 0-3.1.8-3.1 1.9 0 .4.2.8.5 1.1 1 .1 2 .1 3-.1 2.7-.5 5-2.1 6.3-4.3h-5.5c-.7 0-1.2-.2-1.2-.6z"/></svg>`,
  appletv: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.2 7.2c-.5 0-1.1.3-1.5.7-.4.4-.7 1-.6 1.6.6.1 1.2-.2 1.6-.6.4-.4.7-1 .5-1.7zM14.9 9.8c-.9 0-1.6.5-2.1.5s-1.2-.5-2-.5c-1 0-2 .6-2.5 1.5-1.1 1.9-.3 4.7.8 6.2.5.8 1.1 1.6 1.9 1.6.7 0 1-.5 2-.5s1.2.5 2 .5c.8 0 1.4-.8 1.9-1.6.6-.9.8-1.7.8-1.8-.1 0-1.6-.6-1.6-2.5 0-1.6 1.3-2.3 1.3-2.4-.7-1.1-1.9-1.2-2.5-1.5zM4 16.5V8.8h1.4v6.3h3.3v1.4H4z"/></svg>`,
  firetv: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 8.5h2.1c1.5 0 2.4.8 2.4 2.1 0 1.4-.9 2.2-2.4 2.2H5.4v2.7H4V8.5zm1.4 3.1h.6c.7 0 1.1-.3 1.1-1s-.4-1-1.1-1h-.6v2zM10 15.5V8.5h1.4v7H10zm3.2 0V8.5h1.3l2.2 4.5h.1V8.5H18v7h-1.3l-2.2-4.5h-.1v4.5h-1.2zm7.3-2.5l1.5.4c-.3 1.2-1.3 2.1-2.8 2.1-1.9 0-3.2-1.4-3.2-3.5s1.3-3.5 3.2-3.5c1.5 0 2.5.8 2.9 2l-1.5.4c-.2-.7-.7-1.1-1.4-1.1-.9 0-1.6.8-1.6 2.2s.7 2.2 1.6 2.2c.7 0 1.2-.4 1.3-1.2z"/></svg>`,
};

const platforms = [
  { name: 'Windows', icon: platformIcon.windows, to: '/for-pc/' },
  { name: 'macOS', icon: platformIcon.apple, to: '/for-mac/' },
  { name: 'Linux', icon: platformIcon.linux, to: '/for-linux/' },
  { name: 'Android', icon: platformIcon.android, to: '/for-android/' },
  { name: 'iOS', icon: platformIcon.ios, to: '/for-ios/' },
  { name: 'Chrome', icon: platformIcon.chrome },
  { name: 'Firefox', icon: platformIcon.firefox },
  { name: 'Edge', icon: platformIcon.edge },
  { name: 'Apple TV', icon: platformIcon.appletv },
  { name: 'FireTV', icon: platformIcon.firetv },
];

export default {
  name: 'PlatformPage',
  components: {
    HeadNav,
  },
  props: {
    pageKey: {
      type: String,
      required: true,
    },
  },
  computed: {
    page() {
      return secureVpnPlatformPages[this.pageKey];
    },
  },
  data() {
    return {
      platforms,
      platformsTitle: 'Secure VPN Works all Major Platforms',
      platformsLead:
        'From smartphones to desktop computers, Secure VPN is designed to work on the devices you use every day. Install the app, connect to a server, and enjoy a faster, more private browsing experience wherever you go.',
    };
  },
};
</script>

<style lang="scss" scoped>
.platform-page {
  min-width: 0;
  overflow-x: hidden;
  background:
    radial-gradient(ellipse 75% 42% at 85% 0%, rgba(91, 79, 232, 0.09), transparent 58%),
    #f7f6fc;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  margin: 0 auto;
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 62px 0 56px;
  background:
    linear-gradient(140deg, rgba(139, 124, 255, 0.16), transparent 36%),
    linear-gradient(145deg, #12122e 0%, #1e1b4b 58%, #2a2a6a 100%);
  color: #fff;
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 28px 36px;
  align-items: center;
}

.hero-copy {
  max-width: 640px;
  min-width: 0;
}

.hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.hero-platform-image {
  display: block;
  width: 100%;
  max-width: 520px;
  max-height: 430px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.28));
}

.eyebrow {
  margin: 0 0 12px;
  color: var(--vpn-teal-bright);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 18px;
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.025em;
}

.lead {
  margin: 0 0 20px;
  color: rgba(255, 255, 255, 0.84);
  font-size: 17px;
  line-height: 1.75;
}

.subtitle {
  margin: 0 0 16px;
  color: var(--vpn-teal-bright);
  font-size: 19px;
  font-weight: 800;
  line-height: 1.45;
}

.hero-points {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
  margin: 0 0 26px !important;
  padding: 0 !important;
  list-style: none !important;
  list-style-type: none !important;
}

.hero-points li {
  position: relative;
  padding-left: 21px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  line-height: 1.5;
}

.hero-points li::before {
  content: '';
  position: absolute;
  top: 7px;
  left: 0;
  width: 9px;
  height: 5px;
  border-left: 2px solid var(--vpn-teal-bright);
  border-bottom: 2px solid var(--vpn-teal-bright);
  transform: rotate(-45deg);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0 26px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, var(--vpn-teal-bright), var(--vpn-teal));
  color: #fff;
  box-shadow: 0 14px 28px rgba(91, 79, 232, 0.3);
}

.btn-secondary {
  border-color: rgba(255, 255, 255, 0.32);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.section {
  padding: 70px 0;
  scroll-margin-top: 90px;
}

.section-tint {
  background: linear-gradient(180deg, rgba(91, 79, 232, 0.055), rgba(91, 79, 232, 0.015));
}

.section-inner {
  max-width: 1040px;
}

.section-head {
  margin-bottom: 28px;
}

.section-head h2,
.closing h2 {
  margin: 0;
  font-size: clamp(27px, 3.1vw, 38px);
  font-weight: 800;
  line-height: 1.18;
  letter-spacing: -0.02em;
}

.section-head p {
  max-width: 820px;
  margin: 12px 0 0;
  color: var(--vpn-ink-soft);
  font-size: 16px;
  line-height: 1.75;
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.platform-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 118px;
  padding: 18px 12px;
  border: 1px solid var(--vpn-line);
  border-radius: 16px;
  background: #fff;
  box-shadow: var(--vpn-shadow-soft);
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.platform-card:hover {
  border-color: rgba(91, 79, 232, 0.35);
  transform: translateY(-4px);
  box-shadow: var(--vpn-shadow);
}

.platform-icon {
  display: grid;
  width: 42px;
  height: 42px;
  color: var(--vpn-navy-mid);
  place-items: center;
}

.platform-icon::v-deep svg {
  width: 34px;
  height: 34px;
}

.platform-card span {
  color: var(--vpn-ink);
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.block-heading {
  margin: 28px 0 10px;
  color: var(--vpn-ink);
  font-size: 20px;
  font-weight: 800;
  line-height: 1.35;
}

.paragraph {
  margin: 16px 0 16px;
  color: var(--vpn-ink-soft);
  font-size: 16px;
  line-height: 1.8;
}

.content-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 26px;
  margin: 18px 0 24px !important;
  padding: 0 !important;
  list-style: none !important;
  list-style-type: none !important;
}

.content-list li {
  position: relative;
  padding-left: 24px;
  color: var(--vpn-ink-soft);
  font-size: 15px;
  line-height: 1.65;
  list-style: none;
}

.content-list li::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vpn-teal);
  box-shadow: 0 0 0 4px rgba(91, 79, 232, 0.1);
}

.ordered-list {
  grid-template-columns: 1fr;
  counter-reset: platform-step;
}

.ordered-list li {
  min-height: 30px;
  padding-left: 42px;
  counter-increment: platform-step;
}

.ordered-list li::before {
  content: counter(platform-step);
  top: 0;
  left: 0;
  width: 28px;
  height: 28px;
  background: var(--vpn-teal);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  line-height: 28px;
  text-align: center;
  box-shadow: none;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin: 22px 0 4px;
}

.info-card,
.step-card {
  border: 1px solid var(--vpn-line);
  border-radius: 16px;
  background: #fff;
  box-shadow: var(--vpn-shadow-soft);
}

.info-card {
  padding: 22px;
}

.info-card h3,
.step-card h3 {
  margin: 0 0 9px;
  color: var(--vpn-ink);
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
}

.info-card p,
.step-card p {
  margin: 0;
  color: var(--vpn-ink-soft);
  font-size: 15px;
  line-height: 1.7;
}

.steps {
  display: grid;
  gap: 13px;
  margin-top: 22px;
}

.step-card {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  padding: 20px 22px;
}

.step-card > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--vpn-teal), var(--vpn-teal-deep));
  color: #fff;
  font-weight: 800;
}

.table-wrap {
  width: 100%;
  margin: 22px 0 4px;
  overflow-x: auto;
  border: 1px solid var(--vpn-line);
  border-radius: 16px;
  background: #fff;
  box-shadow: var(--vpn-shadow-soft);
}

table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
  text-align: left;
}

th,
td {
  padding: 14px 16px;
  border-bottom: 1px solid var(--vpn-line);
  color: var(--vpn-ink-soft);
  font-size: 14px;
  line-height: 1.55;
  vertical-align: top;
}

th {
  background: rgba(91, 79, 232, 0.08);
  color: var(--vpn-ink);
  font-weight: 800;
}

tr:last-child td {
  border-bottom: 0;
}

.section-cta {
  margin-top: 24px;
}

.faq-list {
  display: grid;
  gap: 12px;
  max-width: none;
}

.faq-item {
  border: 1px solid var(--vpn-line);
  border-radius: 14px;
  background: #fff;
  box-shadow: var(--vpn-shadow-soft);
}

.faq-item summary {
  padding: 18px 20px;
  color: var(--vpn-ink);
  font-size: 16px;
  font-weight: 800;
  line-height: 1.45;
  cursor: pointer;
}

.faq-item p {
  margin: 0;
  padding: 0 20px 19px;
  color: var(--vpn-ink-soft);
  font-size: 15px;
  line-height: 1.7;
}

.closing {
  padding: 64px 0 72px;
  background: linear-gradient(145deg, #12122e 0%, #1e1b4b 58%, #2a2a6a 100%);
  color: #fff;
}

.closing-inner {
  max-width: 820px;
  text-align: center;
}

.closing p {
  margin: 14px 0 26px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.75;
}

@media (max-width: 900px) {
  .platform-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .hero-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }

  .hero-visual {
    order: -1;
  }

  .hero-copy {
    max-width: none;
  }

  .hero-platform-image {
    max-height: none;
  }
}

@media (max-width: 700px) {
  .hero {
    padding: 44px 0;
  }

  .hero-points,
  .content-list,
  .card-grid,
  .platform-grid {
    grid-template-columns: 1fr;
  }

  .section {
    padding: 54px 0;
  }

  .btn {
    width: 100%;
  }

  .hero-actions {
    flex-direction: column;
  }

  .step-card {
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 13px;
    padding: 18px;
  }

  .step-card > span {
    width: 36px;
    height: 36px;
  }
}
</style>
